import type {
  GastronomyAmount,
  GastronomyControls,
  GastronomyCorrection,
  GastronomyDayDetail,
  GastronomyEventKind,
  GastronomyForward,
  GastronomyOutletFigures,
  GastronomyPeriodDetail,
  IsoDate,
} from '@overseer/contracts';
import { gastronomyEventKind } from '@overseer/contracts';
import { addDays } from '@/lib/time';
import { formatAmount, formatCount, formatDecimal, formatZlotyAmount } from '@/lib/format';
import type { GastronomyMoneyMode } from './money-mode';

/**
 * The gastronomy screens' arithmetic, kept out of the components so it can be
 * tested without rendering anything.
 *
 * Money arrives as decimal strings. It stays a string for display and is
 * summed in grosze as a `bigint` ({@link sumDecimal}); a `number` is made only
 * for chart geometry ({@link toAmount}) and for shares, which are ratios.
 */

// ── Dates ───────────────────────────────────────────────────────────────────

/** The API refuses a longer period. Counted inclusive of both ends. */
export const MAX_PERIOD_DAYS = 732;

/** Days from `from` to `to`, both counted: the same date is 1. Negative when reversed. */
export const daySpan = (from: IsoDate, to: IsoDate): number => {
  const utc = (date: IsoDate) => {
    const [y, m, d] = date.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  const days = Math.round((utc(to) - utc(from)) / 86_400_000);
  return days >= 0 ? days + 1 : days - 1;
};

// The period presets and month steps are shared with the hotel: `lib/period.ts`.
export { monthStart, monthStep, periodPresets, wholeMonth, type PeriodPreset } from '@/lib/period';
/** Every date from `from` to `to`, both included. Empty when reversed. */
export const datesBetween = (from: IsoDate, to: IsoDate): IsoDate[] => {
  const out: IsoDate[] = [];
  for (let date = from; date <= to && out.length <= MAX_PERIOD_DAYS; date = addDays(date, 1)) {
    out.push(date);
  }
  return out;
};

// ── Money ───────────────────────────────────────────────────────────────────

/** A decimal string as a number, for chart lengths only. Bad or absent is 0. */
export const toAmount = (value: string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** A revenue figure to the whole złoty: `"12345.67"` → `12 346 zł`. */
export const formatGross = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? '—' : formatZlotyAmount(value);

/** A price or an average, with grosze: `"87.5"` → `87,50 zł`. */
export const formatPrice = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? '—' : formatAmount(value);

/** {@link formatGross} for a figure that may already be a number, as a delta line gets. */
export const formatGrossValue = (value: string | number): string =>
  formatGross(typeof value === 'number' ? value.toFixed(2) : value);

/** A count with grouped thousands. */
export const formatCountValue = (value: string | number): string => formatCount(Number(value));

/** A sold quantity: `"12.000"` → `12`, `"2.5"` → `2,5`. */
export const formatQuantity = (value: string): string => formatDecimal(toAmount(value), 2);

/** Decimal strings added exactly, in grosze. */
export const sumDecimal = (values: ReadonlyArray<string>): string => {
  const grosze = values.reduce((total, value) => {
    const negative = value.trim().startsWith('-');
    const [whole = '0', fraction = ''] = value.trim().replace('-', '').split('.');
    const cents = BigInt(whole || '0') * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2) || '0');
    return total + (negative ? -cents : cents);
  }, 0n);
  const negative = grosze < 0n;
  const abs = negative ? -grosze : grosze;
  const cents = String(abs % 100n).padStart(2, '0');
  return `${negative ? '-' : ''}${abs / 100n}.${cents}`;
};

/** `part` as a percentage of `whole`; null when there is no whole. */
export const shareOf = (part: number, whole: number): number | null =>
  whole > 0 ? (part / whole) * 100 : null;

// ── Outlets ─────────────────────────────────────────────────────────────────

/** The outlets in the order the business reports them; colour follows this. */
export const KNOWN_OUTLETS = ['restaurant', 'cafe', 'grill', 'pizzeria', 'events', 'riding'] as const;

/**
 * Six fills from the three chart tokens: each token, then each mixed towards
 * the foreground — darker in the light theme, lighter in the dark one — so the
 * second three stay apart from the first in both.
 */
export const OUTLET_PALETTE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'color-mix(in oklab, var(--chart-1) 55%, var(--fg))',
  'color-mix(in oklab, var(--chart-2) 55%, var(--fg))',
  'color-mix(in oklab, var(--chart-3) 55%, var(--fg))',
] as const;

/**
 * Sixteen fills for menu categories: the chart and status tokens, then each
 * mixed towards the foreground. A category takes its fill by its place in the
 * outlet's set order, so it keeps its colour from day to day and in both
 * views, and neighbours in that order never share one.
 */
const CATEGORY_BASE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--warning)',
  'var(--info)',
  'var(--danger)',
  'var(--success)',
  'var(--accent)',
] as const;

export const CATEGORY_PALETTE = [
  ...CATEGORY_BASE,
  ...CATEGORY_BASE.map((token) => `color-mix(in oklab, ${token} 55%, var(--fg))`),
] as const;

/** A category's fill by its `order`; what no category takes is neutral. */
export const categoryFill = (order: number | null): string =>
  order === null ? 'var(--fg-subtle)' : (CATEGORY_PALETTE[order % CATEGORY_PALETTE.length] ?? 'var(--fg-subtle)');

/** Revenue under no outlet: neutral, so it never reads as one of them. */
export const NO_OUTLET_FILL = 'var(--fg-subtle)';

/** The series key for an outlet code; `__none` for revenue under no outlet. */
export const NO_OUTLET_KEY = '__none';
export const seriesKey = (code: string | null): string => code ?? NO_OUTLET_KEY;

/** An outlet's fill. The entity decides it, never its rank on the day. */
export const outletFill = (code: string | null): string => {
  if (code === null) return NO_OUTLET_FILL;
  const known = (KNOWN_OUTLETS as ReadonlyArray<string>).indexOf(code);
  if (known >= 0) return OUTLET_PALETTE[known % OUTLET_PALETTE.length] ?? NO_OUTLET_FILL;
  let hash = 0;
  for (const char of code) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return OUTLET_PALETTE[hash % OUTLET_PALETTE.length] ?? NO_OUTLET_FILL;
};

/** Codes in reporting order: known ones first, then the rest by name, no-outlet last. */
export const orderOutletCodes = (codes: Iterable<string | null>): Array<string | null> => {
  const unique = [...new Set(codes)];
  const rank = (code: string | null) => {
    if (code === null) return Number.MAX_SAFE_INTEGER;
    const known = (KNOWN_OUTLETS as ReadonlyArray<string>).indexOf(code);
    return known >= 0 ? known : KNOWN_OUTLETS.length;
  };
  return unique.sort((a, b) => rank(a) - rank(b) || (a ?? '').localeCompare(b ?? ''));
};

/** Outlet rows in reporting order, revenue under no outlet last. */
export const sortOutletFigures = <T extends { outlet: { code: string } | null }>(rows: ReadonlyArray<T>): T[] => {
  const order = orderOutletCodes(rows.map((row) => row.outlet?.code ?? null));
  const rank = (row: T) => order.indexOf(row.outlet?.code ?? null);
  return rows.slice().sort((a, b) => rank(a) - rank(b));
};

/** One chart series per outlet code, in reporting order. */
export const outletSeries = (
  codes: Iterable<string | null>,
  label: (code: string | null) => string,
): Array<{ key: string; name: string; fill: string }> =>
  orderOutletCodes(codes).map((code) => ({
    key: seriesKey(code),
    name: label(code),
    fill: outletFill(code),
  }));

/** Code → name, from the outlets a response carries. */
export const outletNames = (outlets: ReadonlyArray<GastronomyOutletFigures>): Map<string, string> =>
  new Map(
    outlets.flatMap((row) => (row.outlet ? [[row.outlet.code, row.outlet.name] as const] : [])),
  );

// ── Chart rows ──────────────────────────────────────────────────────────────

export type StackedRow = {
  key: string;
  /** Per series key. Missing series are 0. */
  values: Record<string, number>;
  /** Last year's total at this position, when the chart draws it. */
  lastYear?: number;
};

/**
 * The trading day starts at 05:00: a bill closed at half past midnight belongs
 * after 23:00, not before breakfast.
 */
const TRADING_DAY_START = 5;
const tradingOrder = (hour: number) => (hour + 24 - TRADING_DAY_START) % 24;

export type DayHourRow = {
  hour: number;
  value: number;
  /** Null when there was no week to compare with. */
  median: number | null;
  bills: number;
};

/**
 * The day's revenue per closing hour, with the hour's median: one row per hour
 * from the first with revenue to the last in trading order, the quiet hours
 * between kept as zeros.
 */
export const dayHourRows = (hours: GastronomyDayDetail['hours'], mode: GastronomyMoneyMode): DayHourRow[] => {
  if (hours.length === 0) return [];
  const byHour = new Map(hours.map((row) => [row.hour, row]));
  const orders = hours.map((row) => tradingOrder(row.hour));
  const first = Math.min(...orders);
  const last = Math.max(...orders);
  const hasMedian = hours.some((row) => row.median !== null);
  const out: DayHourRow[] = [];
  for (let order = first; order <= last; order += 1) {
    const hour = (order + TRADING_DAY_START) % 24;
    const row = byHour.get(hour);
    out.push({
      hour,
      value: toAmount(row?.[mode]),
      median: hasMedian ? toAmount(row?.median?.[mode]) : null,
      bills: row?.bills ?? 0,
    });
  }
  return out;
};

/**
 * `current` against `typical` as a whole percentage; null when typical is 0,
 * where a percentage says nothing.
 */
export const changeAgainst = (current: number, typical: number): number | null =>
  typical === 0 ? null : Math.round(((current - typical) / Math.abs(typical)) * 100);

/**
 * One outlet's revenue per day, with last year's for the line. Every date of
 * the period is a row, so a closed day shows as a gap.
 */
export const dailyRows = (
  daily: GastronomyPeriodDetail['daily'],
  outletCode: string,
  from: IsoDate,
  to: IsoDate,
  mode: GastronomyMoneyMode,
): StackedRow[] => {
  const rows = new Map<string, StackedRow>(
    datesBetween(from, to).map((date) => [date, { key: date, values: {}, lastYear: 0 }]),
  );
  const key = seriesKey(outletCode);
  for (const row of daily) {
    const target = rows.get(row.date);
    if (!target) continue;
    target.values[key] = (target.values[key] ?? 0) + toAmount(row[mode]);
    target.lastYear = (target.lastYear ?? 0) + toAmount(row.lastYear[mode]);
  }
  return [...rows.values()];
};

export type Grouping = 'day' | 'week' | 'month';

/** A row of a grouped chart: keyed by its first date in the period, and carrying its last. */
export type GroupedRow = StackedRow & { last: string };

/** The Monday of the ISO week a date falls in. */
const weekStart = (date: IsoDate): IsoDate => {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
};

/**
 * Daily rows summed per ISO week (Monday to Sunday) or calendar month, last
 * year's line summed the same way. A week or month the period cuts is summed
 * over the days it has, and `key` and `last` say which those are.
 */
export const groupRows = (rows: ReadonlyArray<StackedRow>, by: Grouping): GroupedRow[] => {
  const groupOf = (date: string) =>
    by === 'day' ? date : by === 'month' ? date.slice(0, 7) : weekStart(date as IsoDate);
  const out: GroupedRow[] = [];
  for (const row of rows) {
    const current = out[out.length - 1];
    if (current && groupOf(current.key) === groupOf(row.key)) {
      for (const [key, value] of Object.entries(row.values)) {
        current.values[key] = (current.values[key] ?? 0) + value;
      }
      current.lastYear = (current.lastYear ?? 0) + (row.lastYear ?? 0);
      current.last = row.key;
    } else {
      out.push({ key: row.key, last: row.key, values: { ...row.values }, lastYear: row.lastYear });
    }
  }
  return out;
};

// ── Heatmap ─────────────────────────────────────────────────────────────────

export type HeatmapMetric = 'bills' | 'revenue';

export type Heatmap = {
  /** The hours shown, from the first with bills to the last. */
  hours: number[];
  /** ISO weekday 1–7, then one value per entry of `hours`. */
  rows: Array<{ weekday: number; values: number[] }>;
  max: number;
};

export const heatmapGrid = (
  cells: GastronomyPeriodDetail['heatmap'],
  metric: HeatmapMetric,
  mode: GastronomyMoneyMode,
): Heatmap => {
  if (cells.length === 0) return { hours: [], rows: [], max: 0 };
  const first = Math.min(...cells.map((cell) => cell.hour));
  const last = Math.max(...cells.map((cell) => cell.hour));
  const hours = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const rows = Array.from({ length: 7 }, (_, i) => ({
    weekday: i + 1,
    values: hours.map(() => 0),
  }));
  let max = 0;
  for (const cell of cells) {
    const value = metric === 'bills' ? cell.bills : toAmount(cell[mode]);
    const row = rows[cell.weekday - 1];
    if (!row) continue;
    const next = (row.values[cell.hour - first] ?? 0) + value;
    row.values[cell.hour - first] = next;
    max = Math.max(max, next);
  }
  return { hours, rows, max };
};

/**
 * The strength of a heatmap cell as a mix percentage: 0 for nothing, then
 * 12–100 so the faintest cell with bills is still told apart from an empty one.
 */
export const heatStrength = (value: number, max: number): number =>
  value <= 0 || max <= 0 ? 0 : Math.round(12 + (value / max) * 88);

// ── Forward ─────────────────────────────────────────────────────────────────

export type ReservationDay = {
  date: IsoDate;
  reservations: number;
  covers: number;
  cancelled: number;
  cancelledCovers: number;
  pointsOfSale: Array<{ name: string | null; pointOfSale: number; covers: number }>;
};

/** Reservations per date, every point of sale added up, earliest first. */
export const reservationDays = (rows: GastronomyForward['reservations']): ReservationDay[] => {
  const days = new Map<string, ReservationDay>();
  for (const row of rows) {
    const day = days.get(row.date) ?? {
      date: row.date,
      reservations: 0,
      covers: 0,
      cancelled: 0,
      cancelledCovers: 0,
      pointsOfSale: [],
    };
    day.reservations += row.reservations;
    day.covers += row.covers;
    day.cancelled += row.cancelled;
    day.cancelledCovers += row.cancelledCovers;
    day.pointsOfSale.push({
      name: row.pointOfSaleName,
      pointOfSale: row.pointOfSale,
      covers: row.covers,
    });
    days.set(row.date, day);
  }
  return [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
};

export type EventMonth = GastronomyAmount & {
  month: string;
  byKind: Partial<Record<GastronomyEventKind, GastronomyAmount & { count: number }>>;
  count: number;
  /** Event bookings (`KALH`) dated in the month. */
  bookings: number;
};

/**
 * The event pipeline as a month × kind table, with the bookings count of the
 * same month beside it. `kinds` holds only the kinds that occur, in the
 * contract's order.
 */
export const eventPipeline = (
  events: GastronomyForward['events'],
  bookings: GastronomyForward['bookings'],
): { kinds: GastronomyEventKind[]; months: EventMonth[] } => {
  const months = new Map<string, EventMonth>();
  const month = (key: string) => {
    const existing = months.get(key);
    if (existing) return existing;
    const created: EventMonth = { month: key, byKind: {}, count: 0, gross: '0.00', nett: '0.00', bookings: 0 };
    months.set(key, created);
    return created;
  };
  const seen = new Set<GastronomyEventKind>();
  for (const row of events) {
    const target = month(row.month);
    const cell = target.byKind[row.kind] ?? { count: 0, gross: '0.00', nett: '0.00' };
    target.byKind[row.kind] = {
      count: cell.count + row.count,
      gross: sumDecimal([cell.gross, row.gross]),
      nett: sumDecimal([cell.nett, row.nett]),
    };
    target.count += row.count;
    target.gross = sumDecimal([target.gross, row.gross]);
    target.nett = sumDecimal([target.nett, row.nett]);
    seen.add(row.kind);
  }
  for (const row of bookings) {
    month(row.month).bookings += row.count;
  }
  return {
    kinds: gastronomyEventKind.options.filter((kind) => seen.has(kind)),
    months: [...months.values()].sort((a, b) => a.month.localeCompare(b.month)),
  };
};

/** Pickup over `days`; null when the snapshot does not carry that window. */
export const pickupFor = (
  pickup: GastronomyForward['pickup'],
  days: number,
): { reservations: number; covers: number } | null =>
  pickup.find((row) => row.days === days) ?? null;

/** Izzy's bill id as the till prints it, without the leading zeros: `001600085296` → `1600085296`. */
export const billNumber = (id: string): string => id.replace(/^0+(?=.)/, '');

// ── Corrections ─────────────────────────────────────────────────────────────

/** A document's number as the till prints it: `KFVS 1/07/R/2026`. */
export const documentLabel = (d: Pick<GastronomyCorrection, 'type' | 'number' | 'series' | 'issuedOn'>): string =>
  `${d.type} ${d.number}/${d.issuedOn.slice(5, 7)}/${d.series}/${d.issuedOn.slice(0, 4)}`;

/**
 * What a period here differs from the books by, because a correction counts
 * on the day of the sale and the books on its issue date: the corrections
 * moved into the period, less those moved out of it. A correction issued and
 * counted inside the period moves nothing.
 */
export const correctionsDifference = (
  rows: ReadonlyArray<GastronomyCorrection>,
  from: IsoDate,
  to: IsoDate,
  mode: GastronomyMoneyMode,
): { movedIn: string; movedOut: string; difference: string } => {
  const inside = (date: string) => date >= from && date <= to;
  const movedIn = rows.filter((row) => inside(row.saleDay) && !inside(row.issuedOn)).map((row) => row[mode]);
  const movedOut = rows.filter((row) => inside(row.issuedOn) && !inside(row.saleDay)).map((row) => row[mode]);
  const into = sumDecimal(movedIn);
  const outOf = sumDecimal(movedOut);
  return { movedIn: into, movedOut: outOf, difference: sumDecimal([into, outOf.startsWith('-') ? outOf.slice(1) : `-${outOf}`]) };
};

// ── Controls ────────────────────────────────────────────────────────────────

export type AuthoriserDeletes = {
  authorisedBy: string | null;
  count: number;
  outlets: Array<{ outletCode: string | null; count: number }>;
};

/** Authorised deletes per authoriser, most first, each with its outlets. */
export const deletesByAuthoriser = (rows: GastronomyControls['deletes']): AuthoriserDeletes[] => {
  const byAuthoriser = new Map<string | null, AuthoriserDeletes>();
  for (const row of rows) {
    const entry = byAuthoriser.get(row.authorisedBy) ?? {
      authorisedBy: row.authorisedBy,
      count: 0,
      outlets: [],
    };
    entry.count += row.count;
    entry.outlets.push({ outletCode: row.outletCode, count: row.count });
    byAuthoriser.set(row.authorisedBy, entry);
  }
  return [...byAuthoriser.values()]
    .map((entry) => ({ ...entry, outlets: entry.outlets.sort((a, b) => b.count - a.count) }))
    .sort((a, b) => b.count - a.count);
};
